import {Button, Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@tryghost/shade';
import {CreateSitePage} from '@/pages/sites/CreateSitePage';
import {LucideIcon, formatNumber} from '@tryghost/shade/utils';
import {SiteCard} from '@/components/sites/SiteCard';
import {useAuth} from '@/contexts/AuthContext';
import {useEffect, useState} from 'react';
import {useSites} from '@/hooks/useSites';

export function DashboardPage() {
    const {user} = useAuth();
    const workspaceName = user?.name?.trim().split(/\s+/)[0];
    const {data: sites, isLoading, isError, error, refetch} = useSites();
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const count = sites?.length ?? 0;
    const canCreate = !count || sites?.some(site => site.access_type === 'owner');

    useEffect(() => {
        if (!showCreateDialog) {
            return;
        }

        document.body.classList.add('studio-create-dialog-open');
        return () => document.body.classList.remove('studio-create-dialog-open');
    }, [showCreateDialog]);

    return (
        <div className="studio-dashboard">
            <header className="studio-page-heading">
                <div>
                    <p className="studio-eyebrow">{workspaceName ? `${workspaceName}’s workspace` : 'Your workspace'}</p>
                    <h1>Your publications</h1>
                </div>
                {!isLoading && !isError && <p className="studio-heading-note"><span>{formatNumber(count)}</span> {count === 1 ? 'publication' : 'publications'}</p>}
            </header>

            {isLoading ? (
                <div aria-label="Loading publications" className="studio-publication-grid" role="status">
                    {['first', 'second'].map(key => <div key={key} className="studio-card-skeleton" />)}
                </div>
            ) : isError ? (
                <div className="studio-empty" role="alert">
                    <h2>Couldn’t load publications</h2>
                    <p>{error instanceof Error ? error.message : 'Please try again.'}</p>
                    <Button className="studio-button studio-button-secondary" type="button" onClick={() => refetch()}>Try again</Button>
                </div>
            ) : (
                <section aria-label="Your publications" className="studio-publication-grid">
                    {(sites ?? []).map((site, index) => <SiteCard key={site.id} index={index + 1} site={site} />)}
                    {canCreate && <button aria-label="Create a publication" className="studio-create-card" type="button" onClick={() => setShowCreateDialog(true)}>
                        <span aria-hidden="true" className="studio-create-art">
                            <span className="studio-paper studio-paper-back" />
                            <span className="studio-paper studio-paper-front"><svg className="studio-paper-symbol" fill="none" height="30" stroke="currentColor" strokeWidth="2" viewBox="0 0 30 30" width="30"><path d="M15 2v26M2 15h26M6 6l18 18M6 24 24 6" /></svg></span>
                        </span>
                        <span className="studio-create-card-label">Create a publication</span>
                        <span className="studio-create-card-description">There’s an audience for<br />what only you can share.</span>
                        <span aria-hidden="true" className="studio-create-cta"><LucideIcon.Plus size={17} strokeWidth={1.5} /></span>
                    </button>}
                </section>
            )}

            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogContent className="studio-active studio-create-dialog">
                    <DialogClose asChild>
                        <button aria-label="Close create publication" className="studio-create-dialog-close" type="button">
                            <LucideIcon.X size={18} />
                        </button>
                    </DialogClose>
                    <DialogHeader className="studio-create-dialog-heading">
                        <DialogTitle>Create a publication</DialogTitle>
                        <DialogDescription>Choose a name and address for your publication.</DialogDescription>
                    </DialogHeader>
                    <CreateSitePage modal />
                </DialogContent>
            </Dialog>
        </div>
    );
}
